import {
  Button,
  Col,
  Form,
  Input,
  Layout,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Checkbox,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import { getUserRole } from "../../Utils/Common";
import {
  formatNumber,
  formatString,
  handleChangePage,
  handleExportToExcel,
  handleExportToPDF,
  handlePageSizeChange,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";

const Customer = () => {
  const { Option } = Select;
  const [loading,     setLoading]     = useState("");
  const [showModal,   setShowModal]   = useState("");
  const [filterConfig, setFilterConfig] = useState<any>({ page: 1 });
  const [dataSize,    setDataSize]    = useState(0);
  const [form]                        = Form.useForm();
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [dataSource,  setDataSource]  = useState<any[]>([]);
  const [mainCompany, setMainCompany] = useState<any[]>([]);
  const [isBranch,    setIsBranch]    = useState(false);   // ← controls Main Company visibility

  function clearFields() {
    setSelectedRow({});
    setIsBranch(false);
    form.resetFields();
  }

  // ─── Columns ──────────────────────────────────────────────────────────────────
  const columns = [
    { title: "ID No.",          dataIndex: "id_no",          key: "id_no" },
    { title: "Company Name",    dataIndex: "main_company",   key: "main_company" },
    { title: "Branch",          dataIndex: "branch_company", key: "branch_company" },
    { title: "Owner Name",      dataIndex: "owner_name",     key: "owner_name" },
    { title: "Contact Person",  dataIndex: "contact_person", key: "contact_person" },
    { title: "Cellphone No.",   dataIndex: "cellphone_no",   key: "cellphone_no" },
    { title: "Terms (Days)",    dataIndex: "terms",          key: "terms" },
    {
      title: "Credit Limit",
      dataIndex: "credit_limit",
      key: "credit_limit",
      render: (text: number) => <span>{formatNumber(text)}</span>,
    },
    { title: "VAT Type", dataIndex: "vat_type", key: "vat_type" },
  ];

  // ─── Export ───────────────────────────────────────────────────────────────────
  async function exportToExcel() {
    setLoading("export");
    try {
      const response = await getRequest(`customers/get`, { ...filterConfig, page: "" }, true);
      if (response.success) {
        handleExportToExcel(response.data.data, columns, "Customers");
      } else {
        message.error("Failed to fetch data for export.");
      }
    } catch {
      message.error("Error exporting data.");
    } finally {
      setLoading("");
    }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const response = await getRequest(`customers/get`, { ...filterConfig, page: "", page_size: "" }, true);
      if (response.success) {
        const data = response.data.data;
        handleExportToPDF(
          data,
          columns,
          "Customers.pdf",
          { title: "List of Customers", content: `Generated on ${new Date().toLocaleString()}` },
          `Total Records: ${data.length}`
        );
      } else {
        message.error("Failed to fetch data for PDF export.");
      }
    } catch {
      message.error("Error exporting PDF.");
    } finally {
      setLoading("");
    }
  }

  // ─── Submit (create / update) ─────────────────────────────────────────────────
  async function submitForm(data: any) {
    setLoading("submit");
    const response = await postRequest(
      `customers/${showModal}`,
      { customer_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} customer.`
      );
    }
  }

  // ─── Delete ───────────────────────────────────────────────────────────────────
  async function deleteCustomer() {
    setLoading("delete");
    const response = await postRequest(
      `customers/delete`,
      { customer_id: selectedRow.id },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        formatString(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to delete customer."
        )
      );
    }
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────────
  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    const response = await getRequest(`customers/get`, filterConfig, true);
    if (response.success) {
      setDataSize(response.data.size);
      setDataSource(response.data.data);
    }
    setLoading("");
  }

  // ── Fetch main companies (is_branch = 0) for the Select dropdown ──────────────
  async function fetchMainCompanies() {
    setMainCompany([]);
    const response = await getRequest(`companies/get`, {}, true);
    if (response.success) {
      setMainCompany(response.data.data);
    }
  }

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  useEffect(() => {
    if (showModal === "create" || showModal === "update") {
      fetchMainCompanies();
    }
  }, [showModal]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Customers"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Customer"
              onCreate={() => setShowModal("create")}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={exportToExcel}
              onExportToPDF={exportToPDF}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value }))
              }
            />
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col span={24} className="mb-3">
            <Row>
              <Col xs={24} md={12} lg={5} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select branch"
                  value={filterConfig.is_branch || ""}
                  onChange={(value) =>
                    setFilterConfig((prev: any) => ({
                      ...prev,
                      is_branch: value,
                    }))
                  }
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Company</Option>
                  <Option value="0">Main Company</Option>
                  <Option value="1">Branch Company</Option>
                </Select>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <CustomTable
                  data={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={loading === "fetch"}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) => handleChangePage(page, setFilterConfig),
                    onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size, setFilterConfig),
                  }}
                  handleRowClick={(record: any) => {
                    setShowModal("update");
                    setSelectedRow(record);
                    setIsBranch(Number(record.is_branch) === 1);
                    form.setFieldsValue({ ...record });
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      {/* ── Create / Update modal ─────────────────────────────────────────────── */}
      <Modal
        open={showModal === "create" || showModal === "update"}
        title={`${showModal === "create" ? "Add" : "Edit"} Customer`}
        centered
        footer={null}
        onCancel={() => { clearFields(); setShowModal(""); }}
        width={900}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row className="w-full ps-1" gutter={[10, 10]}>

              {/* Is Branch toggle */}
              <Col xs={24} md={12}>
                <Form.Item label=" " name="is_branch" valuePropName="checked">
                  <Checkbox
                    onChange={(e) => {
                      setIsBranch(e.target.checked);
                      // clear main company selection when unchecked
                      if (!e.target.checked) {
                        form.setFieldValue("company_id", null);
                      }
                    }}
                  >
                    Is Branch
                  </Checkbox>
                </Form.Item>
              </Col>

              {/* Main Company — only visible when Is Branch is checked */}
              {isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Main Company"
                    name="company_id"
                    rules={[{ required: true, message: "Please select a main company." }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select main company"
                      optionFilterProp="label"
                      options={mainCompany.map((c: any) => ({
                        value: c.id,
                        label: c.name,
                      }))}
                    />
                  </Form.Item>
                </Col>
              )}

              {/* ID No. */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="ID No."
                  name="id_no"
                  rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              {/* Company Name */}
              {!isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Company Name"
                    name="company_name"
                    rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}

              {isBranch && (
              <Col xs={24} md={12}>
                  <Form.Item
                    label="Branch Company"
                    name="branch_company"
                    rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}

              {/* Owner Name */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Owner Name"
                  name="owner_name"
                  rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              {/* Contact Person */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Contact Person"
                  name="contact_person"
                  rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              {/* Telephone No. */}
              <Col xs={24} md={12}>
                <Form.Item label="Telephone No." name="telephone_no">
                  <Input />
                </Form.Item>
              </Col>

              {/* Cellphone No. */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Cellphone No."
                  name="cellphone_no"
                  rules={[
                    { required: true, message: "This field is required." },
                    { len: 11, message: "Cellphone number must be 11 digits." },
                  ]}
                >
                  <Input maxLength={11} />
                </Form.Item>
              </Col>

              {/* Email */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ type: "email", message: "Please enter a valid email." }]}
                >
                  <Input />
                </Form.Item>
              </Col>

              {/* Fax No. */}
              <Col xs={24} md={12}>
                <Form.Item label="Fax No." name="fax_no">
                  <Input />
                </Form.Item>
              </Col>

              {/* TIN */}
              {!isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item label="TIN" name="tin">
                    <Input />
                  </Form.Item>
                </Col>
              )}

              {/* Address */}
              <Col xs={24}>
                <Form.Item
                  label="Address"
                  name="address"
                  rules={[{ required: true, whitespace: true, message: "This field is required." }]}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>

              {/* Terms — admin only */}
              {getUserRole() === "admin" && !isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Terms (Days)"
                    name="terms"
                    rules={[{ required: true, message: "This field is required." }]}
                  >
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      onKeyDown={(e) => {
                        if (["e", "E", ".", "-", "+"].includes(e.key)) e.preventDefault();
                      }}
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                    />
                  </Form.Item>
                </Col>
              )}

              {/* Credit Limit — admin only */}
              {getUserRole() === "admin" && !isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Credit Limit (₱)"
                    name="credit_limit"
                    rules={[{ required: true, message: "This field is required." }]}
                  >
                    <Input
                      type="number"
                      min={0}
                      onKeyDown={(e) => {
                        const isDot   = e.key === ".";
                        const hasDot  = e.currentTarget.value.includes(".");
                        if (!/[0-9.]/.test(e.key) || (isDot && hasDot)) e.preventDefault();
                      }}
                    />
                  </Form.Item>
                </Col>
              )}

              {/* VAT Type */}
              {!isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="VAT Type"
                    name="vat_type"
                    initialValue="0"
                    rules={[{ required: true, message: "This field is required." }]}
                  >
                    <Radio.Group>
                      <Radio value="vat_in">Vat In</Radio>
                      <Radio value="vat_ex">Vat Ex</Radio>
                      <Radio value="zero_rated">Zero Rated</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}

              {/* 2307 Type */}
              {!isBranch && (
                <Col xs={24} md={12}>
                  <Form.Item
                    label="2307 Type"
                    name="_2307_type"
                    initialValue="0"
                    rules={[{ required: true, message: "This field is required." }]}
                  >
                    <Radio.Group>
                      <Radio value="0">0%</Radio>
                      <Radio value="2">2%</Radio>
                      <Radio value="7">7%</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              )}

              {/* Actions */}
              <Col xs={24} className="flex justify-end gap-3">
                <Button onClick={() => { setShowModal(""); clearFields(); }}>
                  Cancel
                </Button>
                {showModal === "update" && (
                  <Popconfirm
                    title="Are you sure you want to delete this customer?"
                    onConfirm={deleteCustomer}
                    okText="Yes"
                    okButtonProps={{ loading: loading === "delete" }}
                  >
                    <Button danger loading={loading === "delete"}>Delete</Button>
                  </Popconfirm>
                )}
                <Button htmlType="submit" type="primary" loading={loading === "submit"}>
                  {showModal === "create" ? "Submit" : "Update"}
                </Button>
              </Col>

            </Row>
          </Form>
        </div>
      </Modal>

      <PersistentFooter />
    </Layout>
  );
};

export default Customer;